import { Container } from 'pixi.js';
import type { LevelData } from '../../level/LevelData';
import type { BoardTutorialCaptionCallback, BoardTutorialComponents } from './types';
import { EventScheduler } from '../../../utils/EventScheduler';
import { GameAreaController } from '../../boardMVC/controllers/GameAreaController';

interface BoardTutorialViewConfig {
  levelData: LevelData;
  maxWidth: number;
  maxHeight: number;
  updateCaption?: BoardTutorialCaptionCallback;
}

export class BoardTutorialView extends Container {
  public readonly components: BoardTutorialComponents;

  constructor(config: BoardTutorialViewConfig) {
    super();

    this.components = this.initGameComponents(config);

    this.interactiveChildren = false;
    this.interactive = false;
  }

  public destroy(): void {
    this.removeTweens(true);
    super.destroy({ children: true });
  }

  private initGameComponents({ levelData, maxWidth, maxHeight }: BoardTutorialViewConfig): BoardTutorialComponents {
    const boardAlignContainer = new Container().props({
      parent: this,
    });

    const gameAreaController = new GameAreaController({
      levelData,
      dragContainer: this,
      rewardedAdTrigger: () => Promise.resolve(false),
      isInBoosterMode: () => false,
    });

    const {
      trayModel,
      view: { boardView, trayView },
    } = gameAreaController;

    trayModel.refillItems();

    boardView.props({
      parent: boardAlignContainer,
      scale: 1,
    });

    trayView.props({
      parent: boardAlignContainer,
      scale: 1,
    });

    const boardBounds = boardView.initialBounds;
    const trayBounds = trayView.getLocalBounds();

    boardView.props({
      x: -(boardBounds.width / 2),
    });

    trayView.props({
      x: -(trayBounds.width / 2) - trayBounds.minX,
      y: boardBounds.height + 110,
    });

    boardAlignContainer.scale = Math.min(
      maxWidth / boardAlignContainer.width,
      maxHeight / boardAlignContainer.height,
      2,
    );

    return {
      gameAreaView: gameAreaController.view,
      gameAreaController,
      scheduler: new EventScheduler(),
    };
  }

  public update(deltaTime: number): void {
    this.components.scheduler.update(deltaTime);
    this.components.gameAreaController.update(deltaTime);
  }

  public async fadeIn(): Promise<void> {
    this.props({
      alpha: 0,
    });
    await this.tweenTo({ alpha: 1 }, 500, 'quadOut', true).asyncOnComplete();
  }

  public async fadeOut(): Promise<void> {
    await this.tweenTo({ alpha: 0 }, 500, 'quadOut', true).asyncOnComplete();
  }
}
